import { apiClient } from '../api/apiClient';

export interface ApplicationUser {
  _id: string;
  email: string;
  name: string;
  // profilePictureId?: string;
  city?: string;
  designation?: string;
  state?: string;
  whatsappNumber?:string;
  address?:string;
  pincode?:string;
  gender?:string;
  profilePictureId?:{
    url?:string;
  }
  currentCompanyInfo?: {
    companyname?: string;
    companyUrl?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    pincode?: string;
  };
  jobPreferences?: {
    jobTitle?: string;
    jobDescription?: string;
    jobType?: 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship';
    experienceLevel?: 'entry' | 'junior' | 'mid' | 'senior' | 'manager' | 'head' | 'executive';
    currentSalary?: {
      max?: number;
      currency?: string;
      denomination?: 'thousand' | 'lakh' | 'crore';
      period?: 'yearly' | 'monthly';
    };
    expectedSalary?: {
      max?: number;
      currency?: string;
      denomination?: 'thousand' | 'lakh' | 'crore';
      period?: 'yearly' | 'monthly';
    };
    skills?: string[];
    location?: string[];
    workMode?: 'remote' | 'onsite' | 'hybrid';
    noticeType?: 'immediate' | '15-days' | '30-days' | '60-days' | '90-days';
    resumeId?: {
      id?: string;
      url?: string;
      originalName?: string;
    } | null;
  };
}

export interface JobCategory {
  _id: string;
  name: string;
  description?: string;
}

export interface JobDetails {
  _id: string;
  jobTitle: string;
  jobDescription: string;
  jobCategoryId: JobCategory;
  jobType: 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship';
  experienceLevel: 'entry' | 'junior' | 'mid' | 'senior' | 'manager' | 'head' | 'executive';
  skills: string[];
  location: string[];
  workMode: 'remote' | 'onsite' | 'hybrid';
  applicationDeadline?: string;
  isDeadlinePassed?: boolean;
  daysRemaining?: number;
}

export interface JobSeekerDetails {
  _id: string;
  skills: string[];
  location: string[];
  resumeId?: {
    _id: string;
    originalName: string;
    url: string;
  };
}

export interface ApplicationMetadata {
  daysSinceApplication: number;
  lastUpdated: string;
  canWithdraw: boolean;
}

export interface FeedInfo {
  _id: string;
  userId: ApplicationUser;
  feedType: {
    _id: string;
    name: string;
  };
  content: string;
  title: string;
  visibility: string;
  createdAt: string;
  updatedAt: string;
  stats: {
    views: number;
    likes: number;
    comments: number;
    shares: number;
    bookmarks: number;
  };
}

export interface Application {
  _id: string;
  // feedId: string;
  feedId: {
    _id: string,
    feedType: {
      _id: string,
      name: string
    }
  };
  userId: ApplicationUser;
  appliedAt: string;
  isActive: boolean;
  status?: 'Pending' | 'Shortlisted' | 'Rejected' | 'Under review';
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface MyApplication extends Application {
  feedId: FeedInfo;
  jobDetails?: JobDetails;
  jobSeekerDetails?: JobSeekerDetails;
  applicationMetadata: ApplicationMetadata;
}

export interface ApplicationsListParams {
  page?: number;
  limit?: number;
  sort?: string;
  feedType?: 'job' | 'jobseeker';
}

export interface ApplicationsPaginationData {
  docs: Application[];
  totalDocs: number;
  limit: number;
  totalPages: number;
  page: number;
  pagingCounter: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  prevPage: number | null;
  nextPage: number | null;
}

export interface ApplicationsListResponse {
  success: boolean;
  message: string;
  data: ApplicationsPaginationData;
}

export interface MyApplicationsPaginationData {
  docs: MyApplication[];
  totalDocs: number;
  limit: number;
  totalPages: number;
  page: number;
  pagingCounter: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  prevPage: number | null;
  nextPage: number | null;
}

export interface MyApplicationsListResponse {
  success: boolean;
  message: string;
  data: MyApplicationsPaginationData;
}

export const applicationsService = {
  getApplicationsByFeedId: async (
    feedId: string,
    params: ApplicationsListParams = {}
  ): Promise<ApplicationsListResponse> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.sort) queryParams.append('sort', params.sort);

    const url = `/apply/${feedId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response;
  },

  getMyApplications: async (
    params: ApplicationsListParams = {}
  ): Promise<MyApplicationsListResponse> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.feedType) queryParams.append('feedType', params.feedType);

    const url = `/apply/my-applications${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response;
  },

  exportApplicationsToCSV: async (feedId: string): Promise<Blob> => {
    const response = await apiClient.getBlob(`/apply/${feedId}/export`, {
      headers: {
        'Accept': 'text/csv'
      }
    });
    return response;
  },

  updateApplicationStatus: async (
    feedId: string,
    userId: string,
    status: 'Pending' | 'Shortlisted' | 'Rejected' | 'Under review'
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.put(`/apply/${feedId}/${userId}/status`, {
      status
    });
    return response;
  },
};